import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import {
  cacheSelectionSetItems,
  getCachedSelectionSetItems,
} from "@Features/design-viewer/components/Modals/SelectionSet/selectionSetItemsCache";
import type {
  GetSelectionsRequest,
  GetSelectionsResponse,
  GetSelectionSetItemsRequest,
  GetSelectionSetItemsResponse,
  DeleteSelectionSetsRequest,
  DeleteSelectionSetsResponse,
  CreateNewSelectionRequest,
  CreateNewSelectionResponse,
  AddSelectionSetItemsRequest,
  AddSelectionSetItemsResponse,
  UpdateSelectionNameRequest,
  UpdateSelectionNameResponse,
  DeleteSelectionSetItemsRequest,
  DeleteSelectionSetItemsResponse,
  ReplaceSelectionSetItemsRequest,
  ReplaceSelectionSetItemsResponse,
} from "@ServerTypes/selectionSet/selectionSet.schema.js";

export async function getSelectionList<T extends Exact<GetSelectionsRequest, T>>(data: T) {
  const response = await axiosWrapper.post<GetSelectionsResponse>(
    `${getBackendUrl()}/selectionSet/getSelections`,
    data,
  );
  if (response.data.status && response.data.data) {
    return response.data.data;
  }
}

export const getSelections = async <T extends Exact<GetSelectionsRequest, T>>(
  data: T,
): Promise<{ data: GetSelectionsResponse }> => {
  const response = await axiosWrapper.post<GetSelectionsResponse>(
    `${getBackendUrl()}/selectionSet/getSelections`,
    data,
  );
  return response;
};

export async function getSelectedSelectionSetItemsList<T extends Exact<GetSelectionSetItemsRequest, T>>(
  data: T,
  itemsVersionNo?: number,
) {
  // 1. Serve from IndexedDB if we've already cached this selectionSetId with matching version.
  const cached = await getCachedSelectionSetItems<NonNullable<GetSelectionSetItemsResponse["data"]>[number]>(
    data.selectionSetId,
    itemsVersionNo,
  );
  if (cached) {
    return cached;
  }

  // 2. Cache miss or version mismatch → fetch from backend.
  const response = await axiosWrapper.post<GetSelectionSetItemsResponse>(
    `${getBackendUrl()}/selectionSet/getSelectionSetItems`,
    data,
  );
  if (response.data.status && response.data.data) {
    // 3. Persist in IndexedDB keyed by selectionSetId with itemsVersionNo.
    await cacheSelectionSetItems(data.selectionSetId, response.data.data, itemsVersionNo);
    return response.data.data;
  }
}

export const deleteSelectionSets = async <T extends Exact<DeleteSelectionSetsRequest, T>>(
  data: T,
): Promise<{ data: DeleteSelectionSetsResponse }> => {
  const response = await axiosWrapper.post<DeleteSelectionSetsResponse>(
    `${getBackendUrl()}/selectionSet/deleteSelectionSets`,
    data,
  );
  return response;
};

export const createNewSelection = async <T extends Exact<CreateNewSelectionRequest, T>>(
  data: T,
): Promise<{ data: CreateNewSelectionResponse }> => {
  const response = await axiosWrapper.post<CreateNewSelectionResponse>(
    `${getBackendUrl()}/selectionSet/createNewSelection`,
    data,
  );
  return response;
};

export const addSelectionSetItems = async <T extends Exact<AddSelectionSetItemsRequest, T>>(
  data: T,
): Promise<{ data: AddSelectionSetItemsResponse }> => {
  const response = await axiosWrapper.post<AddSelectionSetItemsResponse>(
    `${getBackendUrl()}/selectionSet/addSelectionSetItems`,
    data,
  );
  return response;
};

export const updateSelectionName = async <T extends Exact<UpdateSelectionNameRequest, T>>(
  data: T,
): Promise<{ data: UpdateSelectionNameResponse }> => {
  const response = await axiosWrapper.post<UpdateSelectionNameResponse>(
    `${getBackendUrl()}/selectionSet/updateSelectionName`,
    data,
  );
  return response;
};

export const deleteSelectionSetItems = async <T extends Exact<DeleteSelectionSetItemsRequest, T>>(
  data: T,
): Promise<{ data: DeleteSelectionSetItemsResponse }> => {
  const response = await axiosWrapper.post<DeleteSelectionSetItemsResponse>(
    `${getBackendUrl()}/selectionSet/deleteSelectionSetItems`,
    data,
  );
  return response;
};

export const replaceSelectionSetItems = async <T extends Exact<ReplaceSelectionSetItemsRequest, T>>(
  data: T,
): Promise<{ data: ReplaceSelectionSetItemsResponse }> => {
  const response = await axiosWrapper.post<ReplaceSelectionSetItemsResponse>(
    `${getBackendUrl()}/selectionSet/replaceSelectionSetItems`,
    data,
  );
  return response;
};
